// StartupStatusContext.js
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const StartupStatusContext = createContext(null);

export const StartupStatusProvider = ({ children }) => {
  const [statuses, setStatuses] = useState({});

  const setStatus = useCallback((key, nextStatus) => {
    if (!key || !nextStatus?.message) {
      return;
    }

    setStatuses((previous) => ({
      ...previous,
      [key]: {
        key,
        message: nextStatus.message,
        priority: Number.isFinite(nextStatus.priority) ? nextStatus.priority : 100,
      },
    }));
  }, []);

  const clearStatus = useCallback((key) => {
    if (!key) {
      return;
    }

    setStatuses((previous) => {
      if (!previous[key]) {
        return previous;
      }

      const nextStatuses = { ...previous };
      delete nextStatuses[key];
      return nextStatuses;
    });
  }, []);

  const orderedStatuses = useMemo(() => {
    return Object.values(statuses).sort((a, b) => {
      if (a.priority !== b.priority) {
        return a.priority - b.priority;
      }

      return String(a.key).localeCompare(String(b.key));
    });
  }, [statuses]);

  const currentStatus = orderedStatuses[0] || null;

  const value = useMemo(() => ({
    clearStatus,
    currentStatus,
    currentStatusMessage: currentStatus?.message || '',
    hasActiveStatus: Boolean(currentStatus),
    orderedStatuses,
    setStatus,
  }), [clearStatus, currentStatus, orderedStatuses, setStatus]);

  return (
    <StartupStatusContext.Provider value={value}>
      {children}
    </StartupStatusContext.Provider>
  );
};

export const useStartupStatus = () => {
  const context = useContext(StartupStatusContext);

  if (!context) {
    throw new Error('useStartupStatus must be used within a StartupStatusProvider');
  }

  return context;
};

export default StartupStatusContext;